import { useState, useCallback, useRef } from 'react';
import { api } from '../services/api.js';

export type AdPlacement = 'rewarded' | 'interstitial';
export type AdState = 'idle' | 'loading' | 'playing' | 'no_fill' | 'cancelled' | 'error' | 'success';

export interface AdsgramShowResult {
  done: boolean;
  description: string;
  state: 'load' | 'render' | 'playing' | 'destroy';
  error: boolean;
}

export interface AdActionResult {
  success: boolean;
  state: AdState;
  error?: string;
  code?: 'NO_FILL' | 'USER_CANCELLED' | 'SDK_UNAVAILABLE' | 'SHOW_ERROR' | 'SEQUENCE_ERROR';
  description?: string;
}

declare global {
  interface Window {
    Adsgram?: {
      init: (params: { blockId: string; debug?: boolean; debugConsole?: boolean }) => {
        show: () => Promise<AdsgramShowResult>;
        destroy?: () => void;
      };
    };
  }
}

// Global controller cache to strictly prevent duplicate SDK initialization
const controllerCache = new Map<string, any>();

function getOrCreateController(blockId: string) {
  if (controllerCache.has(blockId)) {
    return controllerCache.get(blockId);
  }

  if (typeof window !== 'undefined' && window.Adsgram) {
    const isDev = import.meta.env.DEV;
    const controller = window.Adsgram.init({
      blockId,
      debug: isDev,
      debugConsole: isDev,
    });
    controllerCache.set(blockId, controller);
    return controller;
  }

  return null;
}

export function useAdsgram() {
  const [adState, setAdState] = useState<AdState>('idle');
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [isSequenceRunning, setIsSequenceRunning] = useState(false);
  const [currentSequenceStep, setCurrentSequenceStep] = useState<number>(0);
  const activeAttemptRef = useRef<string | null>(null);

  const resetAdState = useCallback(() => {
    setAdState('idle');
    setStatusMessage(null);
    setIsSequenceRunning(false);
    setCurrentSequenceStep(0);
  }, []);

  // Independently configurable AdsGram placements
  const rewardedBlockId =
    import.meta.env.VITE_ADSGRAM_BLOCK_ID_REWARDED || 'block-user-rewarded-1001';
  const interstitialBlockId =
    import.meta.env.VITE_ADSGRAM_BLOCK_ID_INTERSTITIAL || 'block-user-interstitial-1002';

  /**
   * Shows an individual ad placement respecting AdsGram SDK specifications
   */
  const showAd = useCallback(
    async (placementType: AdPlacement = 'rewarded'): Promise<AdActionResult> => {
      const blockId = placementType === 'rewarded' ? rewardedBlockId : interstitialBlockId;

      setAdState('loading');
      setStatusMessage('Preparing advertisement...');

      // 1. Check SDK availability
      if (typeof window === 'undefined' || !window.Adsgram) {
        console.warn(
          `[AdsGram SDK] SDK script not loaded in window. Executing mock simulation for local development (${placementType})`
        );
        setAdState('playing');
        setStatusMessage('Simulating ad playback (Dev Mode)...');
        await new Promise((r) => setTimeout(r, 1200));

        setAdState('success');
        setStatusMessage('Ad completed successfully');
        setTimeout(() => setAdState('idle'), 2000);
        return { success: true, state: 'success' };
      }

      try {
        // 2. Initialize controller without repeated recreation
        const controller = getOrCreateController(blockId);
        if (!controller) {
          throw new Error('Failed to initialize AdsGram ad controller');
        }

        setAdState('playing');
        setStatusMessage(
          placementType === 'rewarded'
            ? 'Playing Reward Video... Watch completely to receive boost'
            : 'Displaying Reward Interstitial... Please wait'
        );

        // 3. Show Ad and await AdsGram ShowPromiseResult
        const result: AdsgramShowResult = await controller.show();

        // 4. Inspect format-specific completion criteria
        // In AdsGram, done is true when user completes rewarded video or closes interstitial
        if (result.done && !result.error) {
          setAdState('success');
          setStatusMessage('Ad completed successfully!');
          setTimeout(() => setAdState('idle'), 2500);
          return { success: true, state: 'success', description: result.description };
        } else {
          setAdState('cancelled');
          const errorMsg = result.description || 'Ad was closed before completion';
          setStatusMessage(errorMsg);
          return {
            success: false,
            state: 'cancelled',
            code: 'USER_CANCELLED',
            error: errorMsg,
          };
        }
      } catch (err: unknown) {
        console.error('[AdsGram Show Exception]', err);

        const rawDesc = err instanceof Object && 'description' in err ? String((err as any).description) : '';
        const rawMsg = err instanceof Error ? err.message : String(err);
        const combined = `${rawDesc} ${rawMsg}`.toLowerCase();

        // Handle specific AdsGram error events
        if (combined.includes('banner not found') || combined.includes('nobanner') || combined.includes('no-fill')) {
          setAdState('no_fill');
          setStatusMessage('No sponsor ads available at this moment. Please try again shortly.');
          return {
            success: false,
            state: 'no_fill',
            code: 'NO_FILL',
            error: 'No advertising inventory available from AdsGram network',
          };
        }

        if (combined.includes('skip') || combined.includes('user') || combined.includes('close')) {
          setAdState('cancelled');
          setStatusMessage('Ad closed before completion');
          return {
            success: false,
            state: 'cancelled',
            code: 'USER_CANCELLED',
            error: 'User closed ad before full view',
          };
        }

        setAdState('error');
        setStatusMessage('Ad playback failed. You may retry without penalty.');
        return {
          success: false,
          state: 'error',
          code: 'SHOW_ERROR',
          error: rawDesc || rawMsg || 'Ad display failed',
        };
      }
    },
    [rewardedBlockId, interstitialBlockId]
  );

  /**
   * Executes the full server-verified Two-Ad Sequence (Reward Interstitial + Reward Video)
   * Required before Main Miner boost/activation
   */
  const playTwoAdSequence = useCallback(
    async (
      onStep1Complete?: () => void
    ): Promise<{
      success: boolean;
      rewardCoins: number;
      error?: string;
    }> => {
      if (isSequenceRunning) {
        return { success: false, rewardCoins: 0, error: 'An ad sequence is already running' };
      }

      setIsSequenceRunning(true);
      setCurrentSequenceStep(1);
      setStatusMessage('Initiating Stage 1/2: Rewarded Interstitial...');

      try {
        // Step 1: Start attempt on server
        const step1Attempt = await api.startAdAttempt('interstitial', 1);
        activeAttemptRef.current = step1Attempt.attempt_id;

        // Display Interstitial ad
        const ad1Result = await showAd('interstitial');
        if (!ad1Result.success) {
          setIsSequenceRunning(false);
          return {
            success: false,
            rewardCoins: 0,
            error: ad1Result.error || 'Stage 1 Interstitial was not completed',
          };
        }

        // Verify Step 1 on server
        await api.completeAdAttempt(step1Attempt.attempt_id);
        if (onStep1Complete) onStep1Complete();

        // Brief delay between steps
        setCurrentSequenceStep(2);
        setStatusMessage('Stage 1 verified! Preparing Stage 2/2: Reward Video...');
        await new Promise((r) => setTimeout(r, 800));

        // Step 2: Start attempt on server
        const step2Attempt = await api.startAdAttempt('rewarded', 2);
        activeAttemptRef.current = step2Attempt.attempt_id;

        // Display Reward Video ad
        const ad2Result = await showAd('rewarded');
        if (!ad2Result.success) {
          setIsSequenceRunning(false);
          return {
            success: false,
            rewardCoins: 0,
            error: ad2Result.error || 'Stage 2 Reward Video was closed prematurely',
          };
        }

        // Complete Step 2 on server (awards boost coins & ledger credit)
        const completionRes = await api.completeAdAttempt(step2Attempt.attempt_id);

        setIsSequenceRunning(false);
        setCurrentSequenceStep(0);
        setStatusMessage('Two-ad sequence verified! Boost granted.');
        return {
          success: true,
          rewardCoins: completionRes.reward_coins,
        };
      } catch (err: unknown) {
        setIsSequenceRunning(false);
        setAdState('error');
        const errMsg = err instanceof Error ? err.message : 'Ad sequence failed';
        setStatusMessage(errMsg);
        return { success: false, rewardCoins: 0, error: errMsg };
      }
    },
    [isSequenceRunning, showAd]
  );

  return {
    showAd,
    playTwoAdSequence,
    adState,
    isPlaying: adState === 'playing' || adState === 'loading' || isSequenceRunning,
    isSequenceRunning,
    currentSequenceStep,
    statusMessage,
    resetAdState,
    rewardedBlockId,
    interstitialBlockId,
  };
}
